import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, CheckCircle2, Inbox, Euro, AlertTriangle, ArrowRight, Star } from "lucide-react";
import { adminApi, euro } from "@/lib/api";
import { AdminCard, PageTitle, StatusPill } from "@/pages/admin/AdminLayout";

export default function AdminOverview() {
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [inq, setInq] = useState([]);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    adminApi.get("/admin/stats").then((r) => setStats(r.data));
    adminApi.get("/admin/bookings").then((r) => setBookings(r.data.slice(0, 6)));
    adminApi.get("/admin/inquiries").then((r) => setInq(r.data.filter((x) => !x.handled).slice(0, 5)));
    adminApi.get("/admin/settings").then((r) => setSettings(r.data));
  }, []);

  const cards = [
    { l: "In attesa di conferma", v: stats?.counts?.pending, icon: CalendarClock, c: "text-amber-700 bg-amber-50", id: "pending" },
    { l: "Confermate in arrivo", v: stats?.upcoming, icon: CheckCircle2, c: "text-emerald-700 bg-emerald-50", id: "upcoming" },
    { l: "Richieste aperte", v: stats?.open_inquiries, icon: Inbox, c: "text-blue-700 bg-blue-50", id: "inquiries" },
    { l: "Valore confermato", v: stats ? euro(stats.revenue) : null, icon: Euro, c: "text-sea bg-sea/15", id: "revenue" },
  ];

  return (
    <div data-testid="admin-overview">
      <PageTitle title="Panoramica" sub="Lo stato delle prenotazioni AMS Travel" />
      {settings && !settings.notify_email && (
        <div className="mb-6 flex gap-3 items-start bg-amber-50 border border-amber-200 text-amber-900 rounded-2xl p-4 text-sm" data-testid="notify-email-warning">
          <AlertTriangle size={18} className="shrink-0 mt-0.5" />
          <p>Non hai ancora impostato l'email per gli avvisi delle nuove prenotazioni. <Link to="/admin/settings" className="underline font-medium">Impostala ora</Link> per ricevere un'email a ogni richiesta.</p>
        </div>
      )}
      {stats?.pending_reviews > 0 && (
        <div className="mb-6 flex gap-3 items-center bg-sage-mist border border-sage-light text-ocean rounded-2xl p-4 text-sm" data-testid="pending-reviews-alert">
          <Star size={18} className="shrink-0 text-sage-dark" />
          <p>{stats.pending_reviews === 1 ? "C'è 1 nuova recensione" : `Ci sono ${stats.pending_reviews} nuove recensioni`} da approvare. <Link to="/admin/reviews" className="underline font-medium">Vai alle recensioni</Link></p>
        </div>
      )}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map(({ l, v, icon: Icon, c, id }) => (
          <AdminCard key={id} className="p-5">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${c}`}><Icon size={18} /></div>
            <p className="mt-4 font-serif text-3xl text-ocean" data-testid={`stat-${id}`}>{v ?? "—"}</p>
            <p className="text-xs text-ocean/55 mt-1">{l}</p>
          </AdminCard>
        ))}
      </div>
      <div className="mt-8 grid grid-cols-1 xl:grid-cols-3 gap-6">
        <AdminCard className="xl:col-span-2 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-serif text-xl text-ocean">Ultime prenotazioni</h2>
            <Link to="/admin/bookings" className="text-sm text-sea inline-flex items-center gap-1" data-testid="overview-all-bookings">Tutte <ArrowRight size={14} /></Link>
          </div>
          {bookings.length === 0 ? <p className="text-sm text-ocean/50 py-8 text-center">Nessuna prenotazione ancora.</p> : (
            <div className="divide-y divide-sand/70">
              {bookings.map((b) => (
                <div key={b.id} className="py-3 flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-ocean truncate">{b.name} · <span className="text-ocean/60 font-normal">{b.tour_title}</span></p>
                    <p className="text-xs text-ocean/50">{b.ref} · {b.date} · {b.guests} ospiti · {euro(b.total)}</p>
                  </div>
                  <StatusPill status={b.status} />
                </div>
              ))}
            </div>
          )}
        </AdminCard>
        <AdminCard className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-serif text-xl text-ocean">Richieste aperte</h2>
            <Link to="/admin/inquiries" className="text-sm text-sea inline-flex items-center gap-1">Tutte <ArrowRight size={14} /></Link>
          </div>
          {inq.length === 0 ? <p className="text-sm text-ocean/50 py-8 text-center">Nessuna richiesta aperta.</p> : (
            <div className="space-y-3">
              {inq.map((i) => (
                <div key={i.id} className="bg-ivory rounded-xl p-3">
                  <p className="text-sm font-medium text-ocean">{i.name}</p>
                  <p className="text-xs text-ocean/55 line-clamp-2">{i.message || i.interests || `${i.date_from || ""} → ${i.date_to || ""}`}</p>
                </div>
              ))}
            </div>
          )}
        </AdminCard>
      </div>
    </div>
  );
}
